import React, { useEffect, useState } from "react";
import {
    CircularProgress,
    Button,
    TextField,
    Box,
    Typography,
    FormControlLabel,
    Switch,
    InputAdornment,
    Avatar,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SchoolIcon from "@mui/icons-material/School";
import HandshakeIcon from "@mui/icons-material/Handshake";
import PaymentIcon from "@mui/icons-material/Payment";
import PaletteIcon from "@mui/icons-material/Palette";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import {
    add_school_api,
    update_school_api,
    get_school_api,
} from "../../DAL/FlightSchools/FlightSchools";
import { create_stripe_customer_api } from "../../DAL/Stripe/Stripe";
import { baseUrl } from "../../config/config";

const inputStyles = {
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#ffffff",
        borderRadius: "9px",
        color: "#1a1a1a",
        "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
        "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
        "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
    },
    "& .MuiInputBase-input": { color: "#1a1a1a" },
    "& .MuiInputLabel-root": { color: "#aaa", fontSize: "14px" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
    "& .MuiFormHelperText-root": { color: "#aaa" },
    "& .MuiInputAdornment-root p": { color: "#6b6b6b" },
};

const SectionCard = ({ icon, title, subtitle, children }) => (
    <Paper
        sx={{
            p: 3,
            backgroundColor: "#ffffff",
            border: "1px solid rgba(26,26,26,0.09)",
            borderRadius: "14px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            mb: 3,
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, pb: 2, borderBottom: "1px solid rgba(26,26,26,0.09)" }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", backgroundColor: "rgba(192,30,46,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {React.cloneElement(icon, { sx: { color: "#c01e2e", fontSize: 20 } })}
            </Box>
            <Box>
                <Typography sx={{ color: "#1a1a1a", fontSize: "15px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>{title}</Typography>
                {subtitle && <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>{subtitle}</Typography>}
            </Box>
        </Box>
        {children}
    </Paper>
);

export default function AddOrUpdateFlightSchool() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { school_id } = useParams();
    const isEdit = Boolean(school_id);

    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [isCreatingStripeCustomer, setIsCreatingStripeCustomer] = useState(false);

    const [formData, setFormData] = useState({
        name: "",
        primary_admin_name: "",
        primary_admin_email: "",
        included_seats: 10,
        monthly_student_fee: 28.99,


        // Branding
        primary_color: "#c01e2e",
        secondary_color: "#1e3a5f",
        accent_color: "#ffc107",
        // AI Usage Settings
        charge_excess_usage: false,
        voice_ai_price_per_minute: 0.40,
        ground_instructor_price_per_request: 0.10,
        // Partner Configuration
        partner_type: "standard",
        partner_duration_days: 0,
        // Stripe
        stripeCustomerId: "",
        // Module Entitlements
        modules: { learn: true, insights: false, tutors: false, adaptPlus: false },
        // Module 3: Tutors config — only meaningful once modules.tutors is on
        tutorsConfig: { allowOpenMarketplace: false, payoutMode: "instructor", defaultHourlyRate: 50 },
    });

    useEffect(() => {
        if (isEdit) {
            loadSchoolData();
        }
    }, [school_id]);

    const loadSchoolData = async () => {
        try {
            const result = await get_school_api(school_id);
            if (result.code === 200) {
                const school = result.school;
                setFormData({
                    name: school.name || "",
                    primary_admin_name: school.primaryAdmin
                        ? `${school.primaryAdmin.first_name || ""} ${school.primaryAdmin.last_name || ""}`.trim()
                        : "",
                    primary_admin_email: school.primaryAdmin?.email || "",
                    included_seats: school.included_seats ?? 10,
                    monthly_student_fee: school.monthly_student_fee ?? 28.99,

                    primary_color: school.branding?.primary_color || "#c01e2e",
                    secondary_color: school.branding?.secondary_color || "#1e3a5f",
                    accent_color: school.branding?.accent_color || "#ffc107",
                    charge_excess_usage: school.ai_settings?.charge_excess_usage ?? false,
                    voice_ai_price_per_minute: school.ai_settings?.voice_ai_price_per_minute ?? 0.40,
                    ground_instructor_price_per_request: school.ai_settings?.ground_instructor_price_per_request ?? 0.10,
                    partner_type: school.partnerConfig?.type || "standard",
                    partner_duration_days: school.partnerConfig?.durationDays ?? 0,
                    stripeCustomerId: school.stripeCustomerId || "",
                    modules: {
                        learn: school.modules?.learn !== false,
                        insights: school.modules?.insights === true,
                        tutors: school.modules?.tutors === true,
                        adaptPlus: school.modules?.adaptPlus === true,
                    },
                    tutorsConfig: {
                        allowOpenMarketplace: school.tutorsConfig?.allowOpenMarketplace === true,
                        payoutMode: school.tutorsConfig?.payoutMode || "instructor",
                        defaultHourlyRate: school.tutorsConfig?.defaultHourlyRate ?? 50,
                    },
                });
                if (school.branding?.logo) {
                    setLogoPreview(baseUrl + school.branding.logo);
                }
                setIsLoading(false);
            } else {
                enqueueSnackbar(result.message || "Failed to load school", { variant: "error" });
                navigate("/flight-schools");
            }
        } catch (error) {
            enqueueSnackbar("Error loading school data", { variant: "error" });
            navigate("/flight-schools");
        }
    };

    const handleChange = (field) => (event) => {
        const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleNumberChange = (field) => (event) => {
        const value = parseFloat(event.target.value) || 0;
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleLogoChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCreateStripeCustomer = async () => {
        setIsCreatingStripeCustomer(true);
        try {
            const result = await create_stripe_customer_api(formData.name, formData.primary_admin_email);
            if (result.code === 200) {
                setFormData((prev) => ({ ...prev, stripeCustomerId: result.data.id }));
                enqueueSnackbar("Stripe customer created successfully", { variant: "success" });
            } else {
                enqueueSnackbar(result.message || "Failed to create Stripe customer", { variant: "error" });
            }
        } catch (error) {
            console.error("Error creating Stripe customer:", error);
            enqueueSnackbar("Error creating Stripe customer", { variant: "error" });
        } finally {
            setIsCreatingStripeCustomer(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.name.trim()) {
            enqueueSnackbar("School name is required", { variant: "error" });
            return;
        }
        if (!isEdit && !formData.primary_admin_name.trim()) {
            enqueueSnackbar("Primary admin name is required", { variant: "error" });
            return;
        }
        if (!isEdit && !formData.primary_admin_email.trim()) {
            enqueueSnackbar("Primary admin email is required", { variant: "error" });
            return;
        }
        if (!isEdit && !formData.stripeCustomerId && formData.partner_type === "standard") {
            enqueueSnackbar("Stripe customer ID is required", { variant: "error" });
            return;
        }

        setIsSubmitting(true);

        try {
            const nameParts = formData.primary_admin_name.trim().split(/\s+/);
            const firstName = nameParts[0] || "";
            const lastName = nameParts.slice(1).join(" ") || "Account";

            const data = {
                name: formData.name,
                primaryAdminFirstName: firstName,
                primaryAdminLastName: lastName,
                primaryAdminEmail: formData.primary_admin_email,
                included_seats: formData.included_seats,
                monthly_student_fee: formData.monthly_student_fee,

                primaryColor: formData.primary_color,
                secondaryColor: formData.secondary_color,
                accentColor: formData.accent_color,
                chargeExcessUsage: formData.charge_excess_usage,
                voiceAiPricePerMinute: formData.voice_ai_price_per_minute,
                groundInstructorPricePerRequest: formData.ground_instructor_price_per_request,
                partnerType: formData.partner_type,
                partnerDurationDays: formData.partner_duration_days,
                stripeCustomerId: formData.stripeCustomerId,
                modules: formData.modules,
                tutorsConfig: formData.tutorsConfig,
            };

            let result;
            if (isEdit) {
                result = await update_school_api(school_id, data);
            } else {
                const submitData = new FormData();
                Object.keys(data).forEach(key => {
                    submitData.append(key, ["modules", "tutorsConfig"].includes(key) ? JSON.stringify(data[key]) : data[key]);
                });
                if (logoFile) {
                    submitData.append("logo", logoFile);
                }
                result = await add_school_api(submitData);
            }

            if (result.code === 200 || result.code === 201 || result.school) {
                enqueueSnackbar(isEdit ? "Flight school updated successfully" : "Flight school created successfully", {
                    variant: "success",
                });
                navigate("/flight-schools");
            } else {
                enqueueSnackbar(result.message || "Operation failed", { variant: "error" });
            }
        } catch (error) {
            console.error("Submit error:", error);
            enqueueSnackbar("An error occurred", { variant: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="circular-progress">
                <CircularProgress color="primary" />
            </div>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "100vh", backgroundColor: "#FAF7F2", fontFamily: "'Rubik', sans-serif" }}>
            <form onSubmit={handleSubmit}>
                {/* Page Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <IconButton onClick={() => navigate("/flight-schools")} sx={{ color: "#6b6b6b", "&:hover": { color: "#c01e2e" } }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{ color: "#1a1a1a", fontSize: "22px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
                                {isEdit ? "Edit Flight School" : "Add Flight School"}
                            </Typography>
                            <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                {isEdit ? "Update school details, billing, and configuration" : "Set up a new partner flight school"}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/flight-schools")}
                            sx={{
                                borderColor: "rgba(26,26,26,0.2)",
                                color: "#6b6b6b",
                                textTransform: "none",
                                fontFamily: "'Rubik', sans-serif",
                                borderRadius: "8px",
                                "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.05)" },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            sx={{
                                backgroundColor: "#c01e2e",
                                textTransform: "none",
                                fontFamily: "'Rubik', sans-serif",
                                borderRadius: "8px",
                                px: 4,
                                boxShadow: "none",
                                "&:hover": { backgroundColor: "#a81828", boxShadow: "none" },
                            }}
                        >
                            {isSubmitting ? (
                                <CircularProgress size={24} sx={{ color: "#fff" }} />
                            ) : isEdit ? (
                                "Update Flight School"
                            ) : (
                                "Create Flight School"
                            )}
                        </Button>
                    </Box>
                </Box>

                {/* Two Column Layout */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
                    {/* ===== LEFT COLUMN ===== */}
                    <Box>
                        {/* Basic Info + Admin */}
                        <SectionCard icon={<SchoolIcon />} title="School Details" subtitle="Basic information and primary admin">
                            <TextField
                                fullWidth
                                label="Flight School Name *"
                                value={formData.name}
                                onChange={handleChange("name")}
                                sx={{ ...inputStyles, mb: 3 }}
                            />
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Admin Name *"
                                    value={formData.primary_admin_name}
                                    onChange={handleChange("primary_admin_name")}
                                    sx={inputStyles}
                                />
                                <TextField
                                    fullWidth
                                    label="Admin Email *"
                                    type="email"
                                    value={formData.primary_admin_email}
                                    onChange={handleChange("primary_admin_email")}
                                    sx={inputStyles}
                                />
                            </Box>
                        </SectionCard>

                        {/* Module Entitlements */}
                        <SectionCard icon={<SchoolIcon />} title="Modules" subtitle="What this school's admins can access">
                            <FormControlLabel
                                control={<Switch checked disabled sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#c01e2e" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c01e2e" } }} />}
                                label={
                                    <Box>
                                        <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>XAir Learn</Typography>
                                        <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>Add & manage students and staff. Always included.</Typography>
                                    </Box>
                                }
                                sx={{ alignItems: "flex-start", mb: 2.5 }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.modules.insights}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, modules: { ...prev.modules, insights: e.target.checked } }))}
                                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#c01e2e" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c01e2e" } }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>XAir Insights</Typography>
                                        <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>Dashboards, per-student progress, accuracy, and engagement analytics.</Typography>
                                    </Box>
                                }
                                sx={{ alignItems: "flex-start", mb: 2.5 }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={Boolean(formData.modules.adaptPlus)}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, modules: { ...prev.modules, adaptPlus: e.target.checked } }))}
                                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#c01e2e" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c01e2e" } }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>Adapt+ for every student</Typography>
                                        <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>Add-on. Each student's exam estimate, pass chance, earliest exam date and round findings, and the same picture for their instructors.</Typography>
                                    </Box>
                                }
                                sx={{ alignItems: "flex-start", mb: 2.5 }}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.modules.tutors}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, modules: { ...prev.modules, tutors: e.target.checked } }))}
                                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#c01e2e" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c01e2e" } }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>XAir Tutors</Typography>
                                        <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>1:1 lesson booking marketplace — students book and pay instructors, XAir handles payouts.</Typography>
                                    </Box>
                                }
                                sx={{ alignItems: "flex-start", mb: formData.modules.tutors ? 2.5 : 0 }}
                            />
                            {formData.modules.tutors && (
                                <Box sx={{ pl: 0.5, pt: 0.5, borderTop: "1px solid rgba(26,26,26,0.09)", mt: 0.5 }}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.tutorsConfig.allowOpenMarketplace}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, tutorsConfig: { ...prev.tutorsConfig, allowOpenMarketplace: e.target.checked } }))}
                                                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#c01e2e" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c01e2e" } }}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>Open marketplace</Typography>
                                                <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>
                                                    Off (default): this school's own roster students can only book this school's own instructors, at 0% platform commission.<br />
                                                    On: this school's instructors become bookable by students from other opted-in schools, and this school's own students can book out too — either direction is charged a 15% platform commission.
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: "flex-start", mb: 2.5, mt: 1 }}
                                    />
                                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 1 }}>
                                        <FormControl fullWidth sx={inputStyles}>
                                            <InputLabel>Payout goes to</InputLabel>
                                            <Select
                                                value={formData.tutorsConfig.payoutMode}
                                                label="Payout goes to"
                                                onChange={(e) => setFormData((prev) => ({ ...prev, tutorsConfig: { ...prev.tutorsConfig, payoutMode: e.target.value } }))}
                                            >
                                                <MenuItem value="instructor">Directly to each instructor</MenuItem>
                                                <MenuItem value="school">Pooled to the school's own account</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            label="Default hourly rate (£)"
                                            type="number"
                                            value={formData.tutorsConfig.defaultHourlyRate}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, tutorsConfig: { ...prev.tutorsConfig, defaultHourlyRate: parseFloat(e.target.value) || 0 } }))}
                                            inputProps={{ min: 0, step: 0.5 }}
                                            sx={inputStyles}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </SectionCard>

                        {/* Contract & Billing */}
                        <SectionCard icon={<PaymentIcon />} title="Contract & Billing" subtitle="Seat allocation and pricing">
                            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Included Seats"
                                    type="number"
                                    value={formData.included_seats}
                                    onChange={handleNumberChange("included_seats")}
                                    inputProps={{ min: 1 }}
                                    sx={inputStyles}
                                />
                                <TextField
                                    fullWidth
                                    label="Monthly Student Fee"
                                    type="number"
                                    value={formData.monthly_student_fee}
                                    onChange={handleNumberChange("monthly_student_fee")}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                    }}
                                    helperText="Default fee per student seat"
                                    sx={inputStyles}
                                />
                            </Box>

                            {/* Stripe Customer */}
                            {!isEdit && (
                                <>
                                    <TextField
                                        fullWidth
                                        label="Stripe Customer ID"
                                        placeholder="cus_xxxxxxxxxx"
                                        value={formData.stripeCustomerId}
                                        onChange={(e) => setFormData((prev) => ({ ...prev, stripeCustomerId: e.target.value }))}
                                        sx={{ ...inputStyles, mb: 2 }}
                                        helperText="Enter existing Stripe customer ID or create one below"
                                    />
                                    {!formData.stripeCustomerId && (
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            onClick={handleCreateStripeCustomer}
                                            disabled={isCreatingStripeCustomer}
                                            sx={{
                                                borderColor: "#c01e2e",
                                                color: "#c01e2e",
                                                "&:hover": { backgroundColor: "rgba(192,30,46,0.1)" },
                                            }}
                                        >
                                            {isCreatingStripeCustomer ? (
                                                <>
                                                    <CircularProgress size={20} sx={{ mr: 1, color: "#c01e2e" }} />
                                                    Creating Stripe Customer...
                                                </>
                                            ) : (
                                                "Create Stripe Customer"
                                            )}
                                        </Button>
                                    )}
                                </>
                            )}
                        </SectionCard>

                        {/* Partner Configuration */}
                        <SectionCard icon={<HandshakeIcon />} title="Partner Configuration" subtitle="Free trials and auto conversion settings">
                            <FormControl fullWidth sx={{ ...inputStyles, mb: 3 }}>
                                <InputLabel sx={{ color: "#aaa", fontSize: "14px", "&.Mui-focused": { color: "#c01e2e" } }}>Partner Type</InputLabel>
                                <Select
                                    value={formData.partner_type}
                                    onChange={handleChange("partner_type")}
                                    label="Partner Type"
                                    sx={{
                                        backgroundColor: "#ffffff",
                                        color: "#1a1a1a",
                                        borderRadius: "9px",
                                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.16)" },
                                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.28)" },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#c01e2e", borderWidth: "1.5px" },
                                        "& .MuiSvgIcon-root": { color: "#c01e2e" },
                                    }}
                                >
                                    <MenuItem value="standard">Standard (Regular Billing)</MenuItem>
                                    <MenuItem value="free_trial">Free Trial</MenuItem>
                                    <MenuItem value="auto_conversion">Auto Conversion</MenuItem>
                                </Select>
                            </FormControl>

                            {formData.partner_type !== "standard" && (
                                <>
                                    <TextField
                                        fullWidth
                                        label={formData.partner_type === "free_trial" ? "Free Trial Duration" : "Auto Conversion Period"}
                                        type="number"
                                        value={formData.partner_duration_days}
                                        onChange={handleNumberChange("partner_duration_days")}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">days</InputAdornment>,
                                        }}
                                        inputProps={{ min: 1 }}
                                        helperText={
                                            formData.partner_type === "free_trial"
                                                ? "After this period, the school will be billed as a regular flight school"
                                                : "After this period, student accounts will be converted to regular paying accounts"
                                        }
                                        sx={{ ...inputStyles, mb: 2 }}
                                    />
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                        {[30, 60, 90, 180, 365].map((days) => (
                                            <Chip
                                                key={days}
                                                label={days < 365 ? `${days} days` : `${days / 365} year`}
                                                onClick={() => setFormData((prev) => ({ ...prev, partner_duration_days: days }))}
                                                sx={{
                                                    cursor: "pointer",
                                                    borderColor: formData.partner_duration_days === days ? "#c01e2e" : "rgba(26,26,26,0.2)",
                                                    color: formData.partner_duration_days === days ? "#fff" : "#6b6b6b",
                                                    backgroundColor: formData.partner_duration_days === days ? "#c01e2e" : "transparent",
                                                    border: "1px solid",
                                                    fontFamily: "'Rubik', sans-serif",
                                                    "&:hover": { backgroundColor: formData.partner_duration_days === days ? "#a81828" : "rgba(26,26,26,0.05)" },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}

                            {formData.partner_type === "free_trial" && (
                                <Typography sx={{ color: "#aaa", fontSize: "12px", mt: 2, fontStyle: "italic", fontFamily: "'Rubik', sans-serif" }}>
                                    💡 Free trial schools are not billed until the trial period ends. After expiry, they transition to regular billing automatically.
                                </Typography>
                            )}
                            {formData.partner_type === "auto_conversion" && (
                                <Typography sx={{ color: "#aaa", fontSize: "12px", mt: 2, fontStyle: "italic", fontFamily: "'Rubik', sans-serif" }}>
                                    💡 After the conversion period, student accounts will be flagged for migration to regular paying accounts.
                                </Typography>
                            )}
                        </SectionCard>
                    </Box>

                    {/* ===== RIGHT COLUMN ===== */}
                    <Box>
                        {/* Branding */}
                        <SectionCard icon={<PaletteIcon />} title="Branding" subtitle="White label appearance">
                            <Box sx={{ display: "flex", gap: 3, mb: 3, alignItems: "flex-start" }}>
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                                    <Avatar
                                        src={logoPreview}
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            backgroundColor: formData.primary_color,
                                            border: "2px solid rgba(26,26,26,0.12)",
                                        }}
                                    >
                                        <SchoolIcon sx={{ fontSize: 36 }} />
                                    </Avatar>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        size="small"
                                        startIcon={<CloudUploadIcon />}
                                        sx={{
                                            borderColor: "rgba(26,26,26,0.2)",
                                            color: "#6b6b6b",
                                            fontSize: "11px",
                                            textTransform: "none",
                                            fontFamily: "'Rubik', sans-serif",
                                            borderRadius: "7px",
                                            "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.05)" },
                                        }}
                                    >
                                        Upload Logo
                                        <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                                    </Button>
                                </Box>

                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 2, flex: 1 }}>
                                    {[
                                        { label: "Primary", field: "primary_color" },
                                        { label: "Secondary", field: "secondary_color" },
                                        { label: "Accent", field: "accent_color" },
                                    ].map(({ label, field }) => (
                                        <Box key={field}>
                                            <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 0.5, fontFamily: "'Rubik', sans-serif" }}>{label}</Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <input
                                                    type="color"
                                                    value={formData[field]}
                                                    onChange={handleChange(field)}
                                                    style={{ width: 36, height: 36, border: "none", borderRadius: 8, cursor: "pointer" }}
                                                />
                                                <TextField
                                                    size="small"
                                                    value={formData[field]}
                                                    onChange={handleChange(field)}
                                                    sx={{ ...inputStyles, width: 100 }}
                                                />
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            {/* Brand Preview */}
                            <Box
                                sx={{
                                    p: 2.5,
                                    borderRadius: "12px",
                                    background: `linear-gradient(135deg, ${formData.primary_color}15, ${formData.secondary_color}15)`,
                                    border: `1px solid ${formData.primary_color}30`,
                                }}
                            >
                                <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "'Rubik', sans-serif" }}>Preview</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar
                                        src={logoPreview}
                                        sx={{ width: 44, height: 44, backgroundColor: formData.primary_color }}
                                    >
                                        <SchoolIcon />
                                    </Avatar>
                                    <Box>
                                        <Typography sx={{ color: formData.primary_color, fontSize: "16px", fontWeight: 600 }}>
                                            {formData.name || "Flight School Name"}
                                        </Typography>
                                        <Typography sx={{ color: formData.accent_color, fontSize: "11px" }}>
                                            Powered by XAir
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </SectionCard>

                        {/* AI Usage */}
                        <SectionCard icon={<SmartToyIcon />} title="AI Usage & Charging" subtitle="Configure excess usage billing">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.charge_excess_usage}
                                        onChange={handleChange("charge_excess_usage")}
                                        sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#c01e2e" },
                                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#c01e2e" },
                                        }}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography sx={{ color: "#1a1a1a", fontSize: "14px", fontFamily: "'Rubik', sans-serif" }}>
                                            Charge for Excess AI Usage
                                        </Typography>
                                        <Typography sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif" }}>
                                            Excess usage beyond included limits will be billed
                                        </Typography>
                                    </Box>
                                }
                                sx={{ alignItems: "flex-start" }}
                            />

                            {formData.charge_excess_usage && (
                                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 3 }}>
                                    <TextField
                                        fullWidth
                                        label="Voice AI Price"
                                        type="number"
                                        value={formData.voice_ai_price_per_minute}
                                        onChange={handleNumberChange("voice_ai_price_per_minute")}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            endAdornment: <InputAdornment position="end">/ min</InputAdornment>,
                                        }}
                                        inputProps={{ step: 0.01 }}
                                        sx={inputStyles}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Ground Instructor AI"
                                        type="number"
                                        value={formData.ground_instructor_price_per_request}
                                        onChange={handleNumberChange("ground_instructor_price_per_request")}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                            endAdornment: <InputAdornment position="end">/ req</InputAdornment>,
                                        }}
                                        inputProps={{ step: 0.01 }}
                                        sx={inputStyles}
                                    />
                                </Box>
                            )}
                        </SectionCard>
                    </Box>
                </Box>
            </form>
        </Box>
    );
}
